import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getSuperAdminHeader } from './SuperAdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FileText, Filter } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_LABELS = {
  super_login: { label: 'Login', color: 'bg-blue-100 text-blue-700' },
  tenant_created: { label: 'Tenant Criado', color: 'bg-green-100 text-green-700' },
  tenant_updated: { label: 'Tenant Atualizado', color: 'bg-amber-100 text-amber-700' },
  tenant_deleted: { label: 'Tenant Excluído', color: 'bg-red-100 text-red-700' },
};

export function SuperAdminAuditPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState('all');
  const [filterDays, setFilterDays] = useState('30');

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      try {
        let url = `${API}/super/audit-logs?days=${filterDays}&limit=200`;
        if (filterAction !== 'all') url += `&action=${filterAction}`;
        const res = await axios.get(url, { headers: getSuperAdminHeader() });
        setLogs(res.data.logs || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, [filterAction, filterDays]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">Audit Logs</h2>
        <div className="flex items-center gap-3">
          <Select value={filterAction} onValueChange={setFilterAction}>
            <SelectTrigger className="w-44 h-9 text-sm"><Filter className="h-3.5 w-3.5 mr-1.5" /><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as ações</SelectItem>
              <SelectItem value="super_login">Login</SelectItem>
              <SelectItem value="tenant_created">Tenant Criado</SelectItem>
              <SelectItem value="tenant_updated">Tenant Atualizado</SelectItem>
              <SelectItem value="tenant_deleted">Tenant Excluído</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterDays} onValueChange={setFilterDays}>
            <SelectTrigger className="w-32 h-9 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 dias</SelectItem>
              <SelectItem value="30">30 dias</SelectItem>
              <SelectItem value="90">90 dias</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card className="bg-white border-gray-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-8 w-full" />)}</div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <FileText className="h-10 w-10 mb-2" />
              <p>Nenhum registro encontrado</p>
            </div>
          ) : (
            <ScrollArea className="h-[600px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-44">Data/Hora</TableHead>
                    <TableHead className="w-36">Ação</TableHead>
                    <TableHead>Detalhes</TableHead>
                    <TableHead className="w-48">Administrador</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => {
                    const actionInfo = ACTION_LABELS[log.action] || { label: log.action, color: 'bg-gray-100 text-gray-600' };
                    return (
                      <TableRow key={log.id}>
                        <TableCell className="text-sm text-gray-500 font-mono">
                          {new Date(log.timestamp).toLocaleString('pt-BR')}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`text-xs ${actionInfo.color}`}>{actionInfo.label}</Badge>
                        </TableCell>
                        <TableCell className="text-sm">{log.details}</TableCell>
                        <TableCell className="text-sm text-gray-500">{log.admin_email}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
